import React, { useMemo, useEffect } from 'react';
import { Drawer, Box, Typography, Button, IconButton, TextField, Tooltip } from '@mui/material';
import { Close, FileCopyOutlined } from '@mui/icons-material';
import { OccupyFreeSpace, useWindowHotkey } from '../../../toolympus/components/primitives';
import { useFormats } from '../../../toolympus/components/schemed';
import { CalculatorRunner } from '../../../toolympus/components/Calculator/components/CalculatorRunner';
import { CalculatorConfig } from '../../../toolympus/components/Calculator/types';
import { CalculatorConfigRecord } from '../../../toolympus/components/Calculator/storage/useCalculatorConfigsList';
import { CalculationRecord } from './useCalculationsList';
import { runPipeline } from '../../../toolympus/components/Calculator/engine/pipeline';
import { getDefaultCalculatorValues } from '../../../toolympus/components/Calculator/useCalculatorRunnerState';
import { TableDisplay } from '../../../toolympus/components/schemed/TableDisplay';
import { FieldType } from '../../../toolympus/hooks/useSchema';

export interface CalculationDrawerData {
  item: Partial<CalculationRecord> | null;
  update: (changes: Partial<CalculationRecord>) => void;
  save: (extraChanges?: Partial<CalculationRecord>) => Promise<any>;
  cancel: () => void;
  isEditing: boolean;
  isLoading?: boolean;
  hasChanges?: boolean;
}

export interface CalculationEditDrawerProps {
  config?: CalculatorConfigRecord | CalculatorConfig | null;
  data: CalculationDrawerData;
  onCreateBased?: (sourceItem: Partial<CalculationRecord>) => void;
}

export const CalculationEditDrawer: React.FC<CalculationEditDrawerProps> = ({
  config: rawConfig,
  data,
  onCreateBased,
}) => {
  const isDrawerOpen = data.isEditing && !!data.item;
  const item = data.item;

  const formats = useFormats();

  useWindowHotkey('esc', () => {
    if (isDrawerOpen) {
      data.cancel();
    }
  });

  const config: CalculatorConfig | null = useMemo(() => {
    if (item?.calculator_config) {
      return {
        _id: item.reference_id || '',
        title: item.title || '',
        description: '',
        inputs: item.calculator_config.inputs || [],
        rules: item.calculator_config.rules || [],
        outputs: item.calculator_config.outputs || [],
      };
    }
    if (!rawConfig) return null;
    return {
      _id: rawConfig._id || '',
      title: rawConfig.title,
      description: rawConfig.description,
      description_more: (rawConfig as any).description_more,
      inputs: rawConfig.inputs || [],
      rules: rawConfig.rules || [],
      outputs: rawConfig.outputs || [],
    };
  }, [rawConfig, item?.calculator_config, item?.reference_id, item?.title]);

  const currentValues = useMemo(() => {
    return item?.values || {};
  }, [item?.values]);

  // When drawer is opened or config becomes available, ensure default values are populated into the record
  useEffect(() => {
    if (isDrawerOpen && config?.inputs?.length) {
      const current = item?.values || {};
      let needsDefaults = false;
      for (const input of config.inputs) {
        if (current[input.name] === undefined) {
          needsDefaults = true;
          break;
        }
      }
      if (needsDefaults) {
        let initializedValues = getDefaultCalculatorValues(config, current);
        try {
          const result = runPipeline(config, initializedValues);
          initializedValues = { ...initializedValues, ...result.values };
        } catch (e) {
          // ignore
        }
        data.update({ values: initializedValues });
      }
    }
  }, [isDrawerOpen, config, item?.values, data]);

  const handleUpdateInputs = (
    updater:
      | Record<string, any>
      | ((prev: Record<string, any>) => Record<string, any>)
      | string,
    val?: any
  ) => {
    let nextValues: Record<string, any>;
    if (typeof updater === 'string') {
      nextValues = { ...currentValues, [updater]: val };
    } else if (typeof updater === 'function') {
      nextValues = updater(currentValues);
    } else {
      nextValues = updater;
    }

    if (config) {
      try {
        const result = runPipeline(config, nextValues);
        nextValues = { ...nextValues, ...result.values };
      } catch (e) {
        // Keep nextValues as is if pipeline run throws
      }
    }

    data.update({ values: nextValues });
  };

  const handleSave = async () => {
    let valuesToSave = { ...currentValues };
    if (config) {
      try {
        const result = runPipeline(config, valuesToSave);
        valuesToSave = { ...valuesToSave, ...result.values };
      } catch (e) {
        // ignore
      }
    }

    const extraChanges: Partial<CalculationRecord> = { values: valuesToSave };
    if (config && !item?.calculator_config) {
      extraChanges.calculator_config = {
        inputs: config.inputs || [],
        rules: config.rules || [],
        outputs: config.outputs || [],
      };
    }

    await data.save(extraChanges);
  };

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={isDrawerOpen}
      elevation={8}
      PaperProps={{
        sx: {
          width: 600,
        },
      }}
    >
      {isDrawerOpen && (
        <Box display="flex" flexDirection="column" height="100%">
          {/* HEADER (Fixed) */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            py={2}
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="h6">
              {item?._id ? 'Редактирование расчета' : 'Новый расчет'}
            </Typography>

            <OccupyFreeSpace />

            <IconButton size="small" onClick={() => data.cancel()}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* SCROLLABLE CONTENT AREA */}
          <Box flex={1} overflow="auto" px={2} py={2} display="flex" flexDirection="column" gap={2}>
            <TextField
              autoFocus
              size="small"
              fullWidth
              label="Название"
              value={item?.title || ''}
              onChange={(e) => data.update({ title: e.target.value })}
            />

            <TextField
              size="small"
              fullWidth
              multiline
              label="Комментарий"
              value={item?.comment || ''}
              onChange={(e) => data.update({ comment: e.target.value })}
            />

            {config && (
              <Box sx={{ mt: 1 }}>
                <CalculatorRunner
                  config={config}
                  inputValues={currentValues}
                  updateInputValues={handleUpdateInputs}
                  noTitle
                />
              </Box>
            )}

            {item?._id && (
              <Box sx={{ pt: 2, pb: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ userSelect: 'none' }}>
                  Расчет создан{' '}
                  <Tooltip
                    title={
                      <>
                        <TableDisplay
                          field="created_at"
                          row={item}
                          schema={{ type: FieldType.datetime }}
                          config={{ utcToLocal: true }}
                        />
                        {' '}
                        <TableDisplay
                          field="created_by"
                          row={item}
                          schema={{ type: FieldType.dictionarySelect, dictionary: 'Users' }}
                        />
                      </>
                    }
                  >
                    <span>
                      <TableDisplay
                          field="created_at"
                          row={item}
                          schema={{ type: FieldType.datetime }}
                          config={{ utcToLocal: true, format: formats?.date }}
                        />
                    </span>
                  </Tooltip>
                  , обновлен{' '}
                  <Tooltip
                    title={
                      <>
                        <TableDisplay
                          field="updated_at"
                          row={item}
                          schema={{ type: FieldType.datetime }}
                          config={{ utcToLocal: true }}
                        />
                        {' '}
                        <TableDisplay
                          field="updated_by"
                          row={item}
                          schema={{ type: FieldType.dictionarySelect, dictionary: 'Users' }}
                        />
                      </>
                    }
                  >
                    <span>
                      <TableDisplay
                          field="updated_at"
                          row={item}
                          schema={{ type: FieldType.datetime }}
                          config={{ utcToLocal: true, format: formats?.date }}
                        />
                    </span>
                  </Tooltip>
                </Typography>
              </Box>
            )}
          </Box>

          {/* FOOTER (Fixed) */}
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
            px={2}
            py={2}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          >
            {item?._id && onCreateBased && (
              <Tooltip title="Создать на основе">
                <IconButton
                  size="small"
                  onClick={() => onCreateBased(item)}
                >
                  <FileCopyOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Button size="small" onClick={() => data.cancel()}>
              Отмена
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={data.isLoading}
              onClick={handleSave}
            >
              Сохранить
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};
